<template>
  <transition name="fade">
    <div class="modal-container" v-if="show" tabindex="-1" role="dialog">
      <div class="modal vcore_container">
        <button
          class="modal-close"
          @click="$emit('close')"
          @keydown.tab="tabClose"
          ref="closeButton"
          aria-label="Close Modal"
        >
          <i class="vcore_icon icon-close"></i>
        </button>
        <div class="modal-header vcore_row">
          <div class="vcore_col--xs--12">
            <h2 class="title" ref="title" tabindex="-1">
              {{ title }}
            </h2>
          </div>
          <div class="vcore_col--xs--12">
            <div class="sub-title" :class="{ bold: !hasRecomended }">
              {{ subTitle }}
            </div>
          </div>
        </div>
        <transition
          name="fade"
          mode="out-in"
          @beforeLeave="beforeLeave"
          @enter="enter"
          @afterEnter="afterEnter"
        >
          <div class="modal-body" :key="addressType">
            <div v-if="!hasRecomended">
              <div class="vcore_row section">
                <div class="vcore_col--xs--12">
                  <h6>You entered:</h6>
                  <div class="address">{{ enteredAddress | address }}</div>
                </div>
              </div>
            </div>
            <div role="radiogroup" v-else>
              <div class="vcore_row section">
                <div class="vcore_col--xs--12">
                  <h6>You entered</h6>
                  <MailingAddressChoice
                    class="mailing-address"
                    role="radio"
                    ref="selection-entered"
                    :aria-checked="
                      selectedAddress === 'entered' ? 'true' : 'false'
                    "
                    :address="enteredAddress"
                    :selected="selectedAddress === 'entered'"
                    :tabindex="selectedAddress === 'entered' ? '0' : '-1'"
                    :recommended="false"
                    :showRec="true"
                    @click="selectedIndex = 0"
                    @keyup.native="onKeyUp($event, 'entered')"
                  />
                </div>
              </div>
              <div class="vcore_row section">
                <div class="vcore_col--xs--12">
                  <h6>Did you mean?</h6>
                  <MailingAddressChoice
                    class="mailing-address"
                    v-for="(addr, $index) in recAddresses"
                    :key="
                      `${addr.address1}${addr.address2}${addr.city}${
                        addr.state
                      }${addr.zipCode}${addr.plus4}`
                    "
                    role="radio"
                    :ref="`selection-${$index}`"
                    :aria-checked="
                      selectedAddress === $index ? 'true' : 'false'
                    "
                    :address="addr"
                    :selected="selectedAddress === $index"
                    :tabindex="selectedAddress === $index ? '0' : '-1'"
                    :recommended="true"
                    :showRec="recAddresses.length === 1"
                    @click="selectedIndex = $index + 1"
                    @keyup.native="onKeyUp($event, $index)"
                  />
                </div>
              </div>
            </div>
          </div>
        </transition>
        <div class="modal-footer">
          <div class="steps" v-if="numSteps > 1">
            <div
              v-for="i in 2"
              class="step"
              :key="i"
              :class="{ active: i === curStep + 1 }"
            >
              &#8226;
            </div>
          </div>
          <div class="vcore_row">
            <div
              class="vcore_col--xs--12 vcore_col--md--4"
              :class="{
                'vcore_offset--md--4 vcore_push--md--4': !hasRecomended,
                ' vcore_offset--md--8': hasRecomended
              }"
            >
              <Button
                color="blue"
                :block="true"
                size="large"
                @click.native="$emit('continue', selectedAddressObj)"
                ref="continueButton"
                @keydown.tab.native="tabContinue(true, $event)"
                >Continue</Button
              >
            </div>
            <div
              class="vcore_col--xs--12 vcore_col--md--4 vcore_pull--md--4 edit-button"
              v-if="!hasRecomended"
            >
              <Button
                color="blue"
                :block="true"
                variant="secondary"
                size="large"
                @click.native="$emit('close')"
                ref="editButton"
                @keydown.tab.native="tabContinue(false, $event)"
                >Edit address</Button
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import MailingAddressChoice from "./MailingAddressChoice";
import Button from "@Shared/components/Button";

export default {
  name: "MailingModal",
  components: {
    MailingAddressChoice,
    Button
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    recAddresses: {
      type: Array,
      default: () => []
    },
    enteredAddress: Object,
    numSteps: Number,
    curStep: Number,
    addressType: String
  },
  data() {
    return {
      selectedIndex: this.recAddresses.length > 0 ? 1 : 0
    };
  },
  computed: {
    selectedAddressObj() {
      let obj =
        this.selectedAddress === "entered"
          ? { ...this.enteredAddress }
          : { ...this.recAddresses[this.selectedAddress] };
      const plus4 = obj.plus4;
      let zipCode = obj.zipCode;

      delete obj["plus4"];

      if (plus4 !== "" && plus4 !== null && plus4 !== undefined) {
        zipCode += `-${plus4}`;
      }

      return {
        ...obj,
        zipCode
      };
    },
    choices() {
      return ["entered", ...Array(this.recAddresses.length).keys()];
    },
    hasRecomended() {
      return this.recAddresses.length > 0;
    },
    title() {
      return this.hasRecomended
        ? `${this.addressType} address can't be verified`
        : `${this.addressType} address not found`;
    },
    subTitle() {
      return this.hasRecomended
        ? "We can't verify the address you entered but we found a similar match. Select an address and click continue."
        : "The address you entered returned no matches. Please edit your address if it is incorrect.";
    },
    selectedAddress() {
      return this.choices[this.selectedIndex];
    }
  },
  watch: {
    show: {
      handler(val) {
        if (val) {
          document.body.classList.add("modal-open");
        } else {
          this.selectedIndex = 0;
          document.body.classList.remove("modal-open");
        }
      },
      immediate: true
    },
    curStep(val) {
      this.selectedIndex = 0;

      this.$nextTick(() => {
        if (this.$refs.title) {
          this.$refs.title.focus();
        }
      });
    }
  },
  mounted() {
    document.addEventListener("keydown", this.documentEsc);
  },
  destroyed() {
    document.removeEventListener("keydown", this.documentEsc);
  },
  methods: {
    onKeyUp(e, ref) {
      let changeFocus = false;

      // KeyBoard nativgation for custom radio buttons for mailing selection
      switch (e.code) {
        case "Space":
          this.selectedIndex = this.choices.findIndex(x => x === ref);
          break;
        case "ArrowDown":
        case "ArrowRight":
          this.selectedIndex = this.mod(
            this.selectedIndex + 1,
            this.choices.length
          );
          changeFocus = true;
          break;
        case "ArrowUp":
        case "ArrowLeft":
          this.selectedIndex = this.mod(
            this.selectedIndex - 1,
            this.choices.length
          );
          changeFocus = true;
          break;
      }

      if (changeFocus) {
        this.focusChoice(`selection-${this.choices[this.selectedIndex]}`);
      }
    },
    tabClose(e) {
      // Keep focus in modal on shift+tab close button
      if (e.shiftKey) {
        e.preventDefault();

        if (this.hasRecomended) {
          this.$refs.continueButton.$el.focus();
        } else {
          this.$refs.editButton.$el.focus();
        }
      }
    },
    tabContinue(val, e) {
      // Keep focus in modal on tab continue button
      if (!e.shiftKey && val === this.hasRecomended) {
        e.preventDefault();
        this.$refs.closeButton.focus();
      }
    },
    documentEsc(e) {
      if (this.show && e.code === "Escape") {
        this.$emit("close");
      }
    },
    focusChoice(ref) {
      // Work around to handle focus because refs in v-for get put into array
      if (ref.indexOf("entered") !== -1) {
        this.$refs[ref].$el.focus();
      } else {
        this.$refs[ref][0].$el.focus();
      }
    },
    mod(x, n) {
      // Special mod that allows negative numbers
      // -1 % 3 = 2. Used for looping around an array by adding/subtracting to index
      // TODO: Move to utils
      return ((x % n) + n) % n;
    },
    beforeLeave(element) {
      this.prevHeight = getComputedStyle(element).height;
    },
    enter(element) {
      const { height } = getComputedStyle(element);
      element.style.height = this.prevHeight;
      setTimeout(() => {
        element.style.height = height;
      });
    },
    afterEnter(element) {
      element.style.height = "auto";
    }
  }
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.5s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.modal-container {
  overflow-y: auto;
  position: fixed;
  z-index: 2222;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.5);

  .modal {
    position: relative;
    max-width: 750px;
    margin: 0 auto;
    background-color: white;
    padding: 35px;
    top: 100px;
    // transform: translateY(-50%);
  }

  .modal-close {
    position: absolute;
    right: 17px;
    top: 14px;
    font-size: 20px;
    color: #666;
    cursor: pointer;
    background-color: transparent;
    border: 0;
    -webkit-appearance: none;
  }

  .modal-header {
    margin-bottom: 25px;

    .title {
      font-weight: 400;

      &::first-letter {
        text-transform: uppercase;
      }
    }

    .sub-title {
      font-size: 1rem;
      margin-top: 15px;
      line-height: 1.1875rem;

      &.bold {
        font-weight: bold;
      }
    }
  }

  .modal-footer {
    margin-top: 25px;
  }

  .address {
    word-wrap: break-word;
    line-height: 1.1875rem;
  }

  .section {
    &:not(:first-of-type) {
      margin-top: 15px;
    }

    h6 {
      margin-bottom: 5px;
      font-size: 1rem;
    }

    .mailing-address {
      &:not(:last-of-type) {
        margin-bottom: 5px;
      }
    }
  }

  .steps {
    font-size: 30px;
    line-height: 15px;
    text-align: center;
    overflow: hidden;
    margin: 15px 0;

    .step {
      display: inline-block;
      vertical-align: top;
      opacity: 0.2;
    }

    .active {
      font-size: 40px;
      line-height: 15px;
      opacity: 1;
      margin-top: -1px;
    }
  }
}

.edit-button{
  @media(max-width: 760px){
    margin-top: 15px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.5s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>
